/**
 * SingleApplicantForm — "Agregar aspirante individual" dialog (operator).
 *
 * The single-expediente counterpart to the batch ingest: the operator types the
 * applicant's identity (full name + CURP + optional birth date), picks the 3
 * docs (INE / certificado / foto), and submits. Under the EXACER profile the
 * CURP **is** the `expediente_id` (the contract treats the id as opaque; the
 * profile's id rule makes it a CURP — same convention as the carga masiva
 * folders). Flow:
 *   1) `signUploads` declares each document's `tipo` and mints one opaque
 *      `upload_url` per document,
 *   2) each File is PUT directly to its URL (per-file progress),
 *   3) `processSingleExpediente` writes the identity as ground truth, counts the
 *      expediente into the round, and launches the worker for THIS expediente only.
 *
 * A CURP is required: the Capa 2 CURP cross-check is an exact match, so a name
 * alone would deterministically fail it. Adding to an already-finalized round is
 * allowed — live counters update immediately; the report snapshot stays stale
 * until the round is reprocessed (surfaced as a hint on success).
 */

import { useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";

import { api, isApiError } from "../../api/useApi";
import { IS_DEMO, type SignUploadResponse } from "../../api/client";
import { ProblemAlert } from "../../components/ProblemAlert";
import { demoSign } from "./demo";
import { isValidCurp } from "./folderScan";
import { putBytes } from "./putBytes";
import {
  contentTypeFor,
  DOC_KIND_LABEL,
  isRequiredDoc,
  newSlots,
  type DocKind,
  type DocSlot,
} from "./uploadTypes";

type Props = {
  open: boolean;
  roundId: string;
  onClose: () => void;
  /** Bubbles up the expediente_id once the applicant is created + queued. */
  onCreated: (expedienteId: string) => void;
};

function slotChip(slot: DocSlot) {
  switch (slot.status) {
    case "done":
      return (
        <Chip size="small" color="success" variant="outlined"
          icon={<CheckCircleOutlineIcon />} label="Cargado" />
      );
    case "error":
      return (
        <Chip size="small" color="error" variant="outlined"
          icon={<ErrorOutlineIcon />} label="Error" />
      );
    case "signing":
      return <Chip size="small" color="info" variant="outlined" label="Firmando…" />;
    case "uploading":
      return <Chip size="small" color="info" variant="outlined" label={`${slot.progress}%`} />;
    default:
      return slot.file ? (
        <Chip size="small" variant="outlined" label="Listo" />
      ) : (
        <Chip size="small" variant="outlined" label="Sin archivo" />
      );
  }
}

export function SingleApplicantForm({ open, roundId, onClose, onCreated }: Props) {
  const [fullName, setFullName] = useState("");
  const [curp, setCurp] = useState("");
  const [dob, setDob] = useState("");
  const [slots, setSlots] = useState<DocSlot[]>(newSlots);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [conflict, setConflict] = useState(false);

  const fileInputs = useRef<Record<DocKind, HTMLInputElement | null>>({
    ine: null,
    ine_reverso: null,
    certificado: null,
    foto: null,
  });

  const nameValid = fullName.trim().length > 0;
  // The CURP is also the expediente_id (EXACER profile rule) — validate its
  // full shape client-side so the server's PROFILE_ID_VIOLATION never fires.
  const curpValid = isValidCurp(curp.trim());
  // The INE back is optional — only required slots must be picked.
  const allPicked = slots.every((s) => s.file !== null || !isRequiredDoc(s.kind));
  const canSubmit = nameValid && curpValid && allPicked && !busy;

  function reset() {
    setFullName("");
    setCurp("");
    setDob("");
    setSlots(newSlots());
    setError(null);
    setConflict(false);
  }

  function handleClose() {
    if (busy) return; // don't close mid-upload
    reset();
    onClose();
  }

  function pickFile(kind: DocKind, file: File | null) {
    setError(null);
    setSlots((prev) =>
      prev.map((s) =>
        s.kind === kind ? { ...s, file, status: "pending", progress: 0, error: undefined } : s,
      ),
    );
  }

  function setSlot(kind: DocKind, patch: Partial<DocSlot>) {
    setSlots((prev) => prev.map((s) => (s.kind === kind ? { ...s, ...patch } : s)));
  }

  async function handleSubmit() {
    // Under EXACER the CURP IS the expediente_id (profile id rule).
    const id = curp.trim().toUpperCase();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setConflict(false);

    try {
      // 1) Sign one FileSpec per picked doc, each declaring its tipo.
      setSlots((prev) =>
        prev.map((s) => ({ ...s, status: "signing", progress: 0, error: undefined })),
      );
      const files = slots
        .filter((s): s is DocSlot & { file: File } => s.file !== null)
        .map((s) => ({ tipo: s.kind, content_type: contentTypeFor(s.file) }));

      let signed: SignUploadResponse;
      if (IS_DEMO) {
        signed = demoSign(roundId, id, files);
      } else {
        const res = await api.POST("/uploads/sign", {
          body: { round_id: roundId, expediente_id: id, files },
        });
        signed = res.data!;
      }

      // 2) PUT each File to its opaque upload_url, matched back to slots by `tipo`.
      let anyFailed = false;
      await Promise.all(
        signed.uploads.map(async (u) => {
          const kind = u.tipo as DocKind;
          const slot = slots.find((s) => s.kind === kind);
          if (!slot?.file) return;
          setSlot(kind, { status: "uploading", progress: 0 });
          try {
            await putBytes(u.upload_url, slot.file, contentTypeFor(slot.file), (pct) =>
              setSlot(kind, { progress: pct }),
            );
            setSlot(kind, { status: "done", progress: 100 });
          } catch (err) {
            anyFailed = true;
            setSlot(kind, {
              status: "error",
              error: err instanceof Error ? err.message : "Carga fallida.",
            });
          }
        }),
      );
      if (anyFailed) {
        setError(new Error("No se pudieron cargar todos los documentos. Revisa e inténtalo de nuevo."));
        // Re-enable the pickers of slots that DID upload (a 'done' slot's picker is
        // otherwise disabled), so the operator can re-pick or just resubmit — a retry
        // re-signs + re-PUTs every file. Failed slots keep their error indicator.
        setSlots((prev) =>
          prev.map((s) => (s.status === "done" ? { ...s, status: "pending", progress: 0 } : s)),
        );
        return;
      }

      // 3) Write identity as ground truth + count into the round + launch the worker.
      if (!IS_DEMO) {
        await api.POST("/rounds/{round_id}/expedientes/{expediente_id}/process", {
          params: { path: { round_id: roundId, expediente_id: id } },
          body: {
            full_name: fullName.trim(),
            curp: curp.trim(),
            fecha_nacimiento: dob.trim() || null,
          },
        });
      }

      onCreated(id);
      reset();
      onClose();
    } catch (err: unknown) {
      // 409 → a full batch is in flight for this round; the single add is blocked.
      if (isApiError(err) && err.status === 409) {
        setConflict(true);
      } else if (isApiError(err)) {
        setError(err);
      } else {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
      // Reset transient "signing" chips so the operator can retry.
      setSlots((prev) =>
        prev.map((s) => (s.status === "signing" ? { ...s, status: "pending" } : s)),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Agregar aspirante individual</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Captura los datos del aspirante y sus documentos (el reverso del INE es
            opcional). Se procesa con el mismo
            pipeline que el lote y se agrega a la ronda <strong>{roundId}</strong>.
          </Typography>

          <TextField
            label="Nombre completo"
            placeholder="Como aparece en sus documentos"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            size="small"
            required
            fullWidth
            disabled={busy}
            error={!nameValid && fullName.length > 0}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="CURP"
              placeholder="18 caracteres"
              value={curp}
              onChange={(e) => setCurp(e.target.value.toUpperCase())}
              size="small"
              required
              fullWidth
              disabled={busy}
              error={!curpValid && curp.length > 0}
              helperText="Es el ID del expediente bajo el perfil EXACER."
            />
            <TextField
              label="Fecha de nacimiento"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              size="small"
              fullWidth
              disabled={busy}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { max: new Date().toISOString().slice(0, 10) },
              }}
              helperText="Opcional."
            />
          </Stack>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Documentos
            </Typography>
            <Stack spacing={1.5}>
              {slots.map((slot) => (
                <Box key={slot.kind}>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", justifyContent: "space-between" }}>
                    <Stack direction="row" spacing={1.5} sx={{ minWidth: 0, alignItems: "center" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 92 }}>
                        {DOC_KIND_LABEL[slot.kind]}
                      </Typography>
                      <input
                        ref={(el) => {
                          fileInputs.current[slot.kind] = el;
                        }}
                        type="file"
                        accept="image/*,application/pdf"
                        hidden
                        onChange={(e) => pickFile(slot.kind, e.target.files?.[0] ?? null)}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UploadFileOutlinedIcon />}
                        onClick={() => fileInputs.current[slot.kind]?.click()}
                        disabled={busy || slot.status === "done"}
                      >
                        {slot.file ? "Cambiar" : "Elegir archivo"}
                      </Button>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}
                        title={slot.file?.name}
                      >
                        {slot.file?.name ?? "Ningún archivo"}
                      </Typography>
                    </Stack>
                    {slotChip(slot)}
                  </Stack>
                  {slot.status === "uploading" && (
                    <LinearProgress variant="determinate" value={slot.progress} sx={{ mt: 0.75, borderRadius: 1 }} />
                  )}
                  {slot.status === "error" && slot.error && (
                    <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                      {slot.error}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>

          {conflict && (
            <Alert severity="warning" onClose={() => setConflict(false)}>
              <AlertTitle>Ronda en proceso</AlertTitle>
              Esta ronda tiene una corrida por lotes en curso. Espera a que termine antes de
              agregar un aspirante individual.
            </Alert>
          )}
          {error && <ProblemAlert error={error} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<PersonAddAlt1Icon />}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {busy ? "Procesando…" : "Crear y procesar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
